/**
 * Sinematik Oynatıcı Kontrol Çubuğu (Cinema Player Bar).
 *
 * Özellikler:
 *   - İki katmanlı modern düzen: tam genişlikte ray (scrubber) ve ergonomik kontroller.
 *   - « 10sn geri ve 10sn ileri » hızlı atlama butonları.
 *   - Akıllı ses çubuğu ve oynatma hızı seçici (0.5x – 2x).
 *   - Tam ekran ve normal ekranda çalışan Altyazı & Ses Ayarları menüsü.
 *   - Klavye kısayolları klavuz modalı (? veya buton ile açılır).
 *
 * ## Mobil: menü neden "alt sayfa" (bottom sheet) olarak açılıyor?
 *
 * Altyazı ayarları popover'ı düğmeye **yukarıdan** açılır ve oynatıcı
 * kutusuna (`position: absolute`) göre konumlanır. Mobilde oynatıcı kutusu
 * ekranın yalnızca ~%25'ini kaplar (`56.25vw` yükseklik) ve `overflow: hidden`
 * ile sınırlıdır; popover yukarı taşınca **kesilir** — kullanıcı "Boyut",
 * "Renk", "Konum" ve "Arka Plan" ayarlarını göremez.
 *
 * Çözüm iki parçalı:
 *
 *   1. **Portal**: menü DOM'da kutuya (`overlayContainer`) basılır, böylece
 *      kutunun `overflow` sınırı onu kesmez. Kutuyu hedeflemek tam ekranda
 *      da doğrudur: menü en üst katmanda kalır (`document.body`'ye basılsaydı
 *      tam ekranın arkasında gizlenirdi).
 *   2. **Sabit konum**: `position: fixed` + `bottom: 0` ile menü ekranın
 *      altına yerleşir, güvenli alan (`env(safe-area-inset-bottom)`) ve
 *      `dvh` yükseklikle tüm içeriği görünür kılar.
 *
 * Geniş ekranda mevcut açılır menü korunur (portal yalnız dar ekranda).
 */

import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Subtitle, SubtitleState } from '../api.ts';
import { NARROW_QUERY as NARROW_MEDIA_QUERY } from '../breakpoints.ts';
import { formatDuration } from '../format.ts';
import {
  CaptionsIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Forward10Icon,
  FullscreenExitIcon,
  FullscreenIcon,
  ImageIcon,
  KeyboardIcon,
  PauseIcon,
  PlayIcon,
  Replay10Icon,
  VolumeIcon,
} from '../Icon.tsx';
import {
  clampPositionPercent,
  POSITION_MAX_PERCENT,
  POSITION_MIN_PERCENT,
  SIZE_LABELS,
} from '../subtitleStyle.ts';
import { useModalFocus } from '../useModalFocus.ts';
import { barPercent, type PlayerControls } from '../usePlayerControls.ts';
import {
  SUBTITLE_BACKGROUNDS,
  SUBTITLE_COLORS,
  type SubtitleBackground,
  type SubtitleColor,
  type SubtitlePrefsApi,
} from '../useSubtitlePrefs.ts';
import type { VideoFit } from '../videoFit.ts';
import { PlayerNextButton } from './PlayerNextButton.tsx';

const HIZLAR = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

/** Düğmeyle tek adım kaydırma (yüzde). */
const POSITION_STEP = 4;

/**
 * Bu genişliğin altı "dar ekran" (mobil) sayılır.
 *
 * Eşik `breakpoints.ts` içindeki tek kaynaktan gelir; CSS'teki
 * `@media (max-width: 768px)` kurallarıyla aynı değeri paylaşır.
 */
const NARROW_QUERY = NARROW_MEDIA_QUERY;

/**
 * Kaydırmayı insan diliyle anlatır.
 *
 * Sayı göstermek yerine yönü söylemek daha anlaşılır: kullanıcı "−8%"in
 * ne demek olduğunu bilmez ama "Siyah bantta" olduğunu anlar.
 */
function describePosition(percent: number): string {
  if (percent <= -6) return 'Siyah bantta';
  if (percent < 0) return 'Aşağı';
  if (percent === 0) return 'Altyazı dibi';
  if (percent <= 8) return 'Normal';
  return 'Yukarı';
}

const LANGUAGE_NAMES: Record<string, string> = {
  tur: 'Türkçe',
  tr: 'Türkçe',
  eng: 'İngilizce',
  en: 'İngilizce',
  ger: 'Almanca',
  de: 'Almanca',
  fra: 'Fransızca',
  fre: 'Fransızca',
  spa: 'İspanyolca',
  es: 'İspanyolca',
  ita: 'İtalyanca',
  it: 'İtalyanca',
};

/**
 * Dar ekran mı?
 *
 * `matchMedia` **dinleyiciyle** takip edilir: cihaz döndüğünde menü biçimi
 * anında değişir. `matchMedia` olmayan ortamda (test) geniş ekran varsayılır.
 */
function useNarrowViewport(query: string = NARROW_QUERY): boolean {
  const [narrow, setNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (): void => setNarrow(mql.matches);
    onChange();
    if (typeof mql.addEventListener !== 'function') return;
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return narrow;
}

export interface PlayerBarProps {
  controls: PlayerControls;
  /** Katalogdaki gerçek toplam süre (ms). */
  totalMs: number;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  /** Video yerleşimi: sığdır (tam görüntü) / doldur (kenarları kırpar). */
  videoFit: VideoFit;
  onToggleVideoFit: () => void;
  tracks?: Array<Subtitle & { selected: boolean; state?: SubtitleState }>;
  /** Seçilen altyazının **anahtarı** (`null` = kapalı). */
  onSelectTrack?: (key: string | null) => void;
  subtitlePrefs?: SubtitlePrefsApi;
  showShortcuts?: boolean;
  onToggleShortcuts?: () => void;
  /**
   * Menü portalının basılacağı kap (oynatıcı kutusu).
   *
   * Mobilde menü bu kutunun **içine** basılır: kutu `overflow: hidden` ile
   * sınırlı olduğu için menü oradan taşmalıdır, ama tam ekranda menü en üst
   * katmanda kalmalıdır — bu yüzden `document.body` değil, kutu hedeflenir.
   */
  overlayContainer?: HTMLElement | null;
  /**
   * "Sıradaki" düğmesi.
   *
   * Sıradaki içeriğin **ne** olduğuna sunucu karar verir (müzik → sıradaki
   * parça, dizi → sıradaki bölüm); buraya yalnız sonuç ve başlık gelir.
   */
  hasNext?: boolean;
  nextTitle?: string | null;
  onNext?: () => void;
  /** Kontroller görünmezken klavye/ekran okuyucu odağından çıkarılır. */
  controlsInert?: boolean;
}

export function PlayerBar({
  controls,
  totalMs,
  isFullscreen,
  onToggleFullscreen,
  videoFit,
  onToggleVideoFit,
  tracks,
  onSelectTrack,
  subtitlePrefs,
  showShortcuts,
  onToggleShortcuts,
  overlayContainer,
  hasNext = false,
  nextTitle = null,
  onNext,
  controlsInert = false,
}: PlayerBarProps): ReactNode {
  const [showSubMenu, setShowSubMenu] = useState(false);
  /**
   * Menünün basıldığı kap, **açılış anında** dondurulur.
   *
   * Kutunun kimliği render'lar arasında değişirse (ör. oynatma hazır olduğunda)
   * portal hedefi değişir ve açık menü kalmak yerine yeniden kurulurdu.
   */
  const [menuHost, setMenuHost] = useState<HTMLElement | null>(null);
  const isNarrow = useNarrowViewport();

  const selectedTrack = tracks?.find((t) => t.selected);
  const langDisplay = selectedTrack?.language
    ? (LANGUAGE_NAMES[selectedTrack.language.toLowerCase()] ?? selectedTrack.language.toUpperCase())
    : null;
  const subLabel = selectedTrack ? langDisplay || selectedTrack.title || 'Açık' : null;

  const openSubMenu = (): void => {
    setMenuHost(overlayContainer ?? null);
    setShowSubMenu(true);
  };
  const closeSubMenu = (): void => setShowSubMenu(false);

  const hasSubtitleMenu = Boolean(tracks && tracks.length > 0 && onSelectTrack && subtitlePrefs);

  const popover = hasSubtitleMenu ? (
    <SubtitlePopover
      variant={isNarrow ? 'sheet' : 'popover'}
      tracks={tracks ?? []}
      onSelect={(idx) => {
        onSelectTrack?.(idx);
      }}
      prefs={subtitlePrefs as SubtitlePrefsApi}
      onClose={closeSubMenu}
    />
  ) : null;

  /*
   * Mobilde menü **alt sayfa** olarak basılır.
   *
   * Hedef önceliği: oynatıcı kutusu → `document.body`.
   *
   * Kutu, tam ekranda menünün **en üst katmanda** kalmasını sağlar
   * (`body`'ye basılsaydı tam ekranın arkasında gizlenirdi). Kutu yoksa
   * (henüz DOM'a girmedi) `body` kullanılır; menü yine `position: fixed`
   * olduğu için ekranın altında görünür.
   */
  const sheetHost =
    isNarrow && hasSubtitleMenu
      ? (menuHost ?? (typeof document !== 'undefined' ? document.body : null))
      : null;

  const sheetContent = (
    <>
      <div className="player__sheet-scrim" onClick={closeSubMenu} aria-hidden="true" />
      {popover}
    </>
  );

  return (
    <div className="player player--cinema" inert={controlsInert ? true : undefined}>
      {controls.blockedSeek && (
        <p className="player__notice" role="status">
          Bu bölüm henüz kodlanmadı. Dönüştürme sürerken ilerleyecek.
        </p>
      )}

      {/* 1. Satır: Tam Genişlikte İlerleme Rayı (Scrubber) */}
      <Scrubber controls={controls} totalMs={totalMs} />

      {/* 2. Satır: Kontroller (Sol: Oynat/Süre, Sağ: Altyazı/Hız/Ekran) */}
      <div className="player__row">
        <PlaybackActions
          controls={controls}
          totalMs={totalMs}
          hasNext={hasNext}
          nextTitle={nextTitle}
          onNext={onNext}
        />

        <PlayerTools
          controls={controls}
          isFullscreen={isFullscreen}
          onToggleFullscreen={onToggleFullscreen}
          videoFit={videoFit}
          onToggleVideoFit={onToggleVideoFit}
          onToggleShortcuts={onToggleShortcuts}
          hasSubtitleMenu={hasSubtitleMenu}
          showSubMenu={showSubMenu}
          subLabel={subLabel}
          subIsActive={selectedTrack !== undefined}
          onToggleSubMenu={() => (showSubMenu ? closeSubMenu() : openSubMenu())}
          popover={!isNarrow ? popover : null}
        />
      </div>

      {/*
        Mobil alt sayfa menüsü: kutuya **portal** ile basılır, böylece
        kutunun `overflow: hidden` sınırı menüyü kesmez.
      */}
      {showSubMenu && sheetHost !== null && createPortal(sheetContent, sheetHost)}

      {showShortcuts && onToggleShortcuts && <ShortcutsModal onClose={onToggleShortcuts} />}
    </div>
  );
}

/** Sol grup: oynat/duraklat, 10 sn atlat, ses, süre. */
function PlaybackActions({
  controls,
  totalMs,
  hasNext,
  nextTitle,
  onNext,
}: {
  controls: PlayerControls;
  totalMs: number;
  hasNext: boolean;
  nextTitle: string | null;
  onNext?: (() => void) | undefined;
}): ReactNode {
  return (
    <div className="player__group player__group--left">
      <button
        type="button"
        className="player__button player__button--play"
        onClick={controls.togglePlay}
        aria-label={controls.isPlaying ? 'Duraklat (Space)' : 'Oynat (Space)'}
        title={controls.isPlaying ? 'Duraklat (Space)' : 'Oynat (Space)'}
      >
        {controls.isPlaying ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
      </button>

      <button
        type="button"
        className="player__button player__button--step"
        onClick={() => controls.seekRelative(-10_000)}
        aria-label="10 saniye geri sar (sol ok)"
        title="10 saniye geri sar (sol ok veya J)"
      >
        <Replay10Icon size={16} />
        <span className="player__step-label">10</span>
      </button>

      <button
        type="button"
        className="player__button player__button--step"
        onClick={() => controls.seekRelative(10_000)}
        aria-label="10 saniye ileri sar (sağ ok)"
        title="10 saniye ileri sar (sağ ok veya L)"
      >
        <span className="player__step-label">10</span>
        <Forward10Icon size={16} />
      </button>

      {/*
        "Sıradaki" düğmesi oynatma grubunda durur: oynat/duraklat ve atlama
        düğmeleriyle aynı sırada, kullanıcının parmağı zaten orada.
      */}
      <PlayerNextButton hasNext={hasNext} nextTitle={nextTitle} onNext={() => onNext?.()} />

      <VolumeControl
        volume={controls.volume}
        muted={controls.muted}
        onToggleMute={controls.toggleMute}
        onSetVolume={controls.setVolume}
      />

      {/*
        Süre iki parçaya bölünür: mobilde toplam süre gizlenir
        (`player__time-total`), çünkü dar ekranda "12:34 / 1:52:10"
        satırı yerine sayılmaz yer kaplıyordu.
      */}
      <span className="player__time">
        <span className="player__time-now">{formatDuration(controls.positionMs)}</span>
        <span className="player__time-total"> / {formatDuration(totalMs)}</span>
      </span>
    </div>
  );
}

/** Sağ grup: altyazı menüsü, hız, kısayollar, tam ekran. */
function PlayerTools({
  controls,
  isFullscreen,
  onToggleFullscreen,
  videoFit,
  onToggleVideoFit,
  onToggleShortcuts,
  hasSubtitleMenu,
  showSubMenu,
  subLabel,
  subIsActive,
  onToggleSubMenu,
  popover,
}: {
  controls: PlayerControls;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  videoFit: VideoFit;
  onToggleVideoFit: () => void;
  onToggleShortcuts?: (() => void) | undefined;
  hasSubtitleMenu: boolean;
  showSubMenu: boolean;
  subLabel: string | null;
  subIsActive: boolean;
  onToggleSubMenu: () => void;
  popover: ReactNode;
}): ReactNode {
  return (
    <div className="player__group player__group--right">
      {hasSubtitleMenu && (
        <div className="player__sub-wrap">
          <button
            type="button"
            className={`player__button player__button--sub ${subIsActive ? 'is-active' : ''}`}
            onClick={onToggleSubMenu}
            aria-expanded={showSubMenu}
            aria-haspopup="dialog"
            aria-label="Altyazı ve ses ayarları"
            title="Altyazı ve ses ayarları"
          >
            <CaptionsIcon size={16} />
            <span className="player__sub-text"> {subLabel ?? 'Altyazı'}</span>
          </button>

          {showSubMenu && popover}
        </div>
      )}

      <select
        className="player__rate"
        value={String(controls.playbackRate)}
        aria-label="Oynatma hızı"
        onChange={(event) => controls.setPlaybackRate(Number(event.target.value))}
        title="Oynatma Hızı"
      >
        {HIZLAR.map((rate) => (
          <option key={rate} value={String(rate)}>
            {/* "1×" kısa yazım: mobilde seçici dar, "Normal Hız" kırpılıyordu. */}
            {rate === 1 ? '1×' : `${rate}×`}
          </option>
        ))}
      </select>

      {onToggleShortcuts && (
        <button
          type="button"
          className="player__button player__btn-shortcuts"
          onClick={onToggleShortcuts}
          aria-label="Klavye kısayolları (?)"
          title="Klavye kısayolları (?)"
        >
          <KeyboardIcon size={18} />
        </button>
      )}

      <button
        type="button"
        className={`player__button player__btn-fit ${videoFit === 'cover' ? 'is-active' : ''}`}
        onClick={onToggleVideoFit}
        aria-pressed={videoFit === 'cover'}
        aria-label={
          videoFit === 'cover'
            ? 'Görüntüyü sığdır (tam görüntü)'
            : 'Ekranı doldur (kenarlar kırpılır)'
        }
        title={
          videoFit === 'cover'
            ? 'Görüntüyü sığdır: tam görüntü, siyah bant olabilir'
            : 'Ekranı doldur: kenarlar kırpılır, siyah bant kalmaz'
        }
      >
        <ImageIcon size={18} />
        <span className="player__fit-text">{videoFit === 'cover' ? 'Doldur' : 'Sığdır'}</span>
      </button>

      <button
        type="button"
        className="player__button player__btn-fullscreen"
        onClick={onToggleFullscreen}
        aria-label={isFullscreen ? 'Tam ekrandan çık (F)' : 'Tam ekran (F)'}
        title={isFullscreen ? 'Tam ekrandan çık (F)' : 'Tam ekran (F)'}
      >
        {isFullscreen ? <FullscreenExitIcon size={20} /> : <FullscreenIcon size={20} />}
      </button>
    </div>
  );
}

function Scrubber({ controls, totalMs }: { controls: PlayerControls; totalMs: number }): ReactNode {
  const { availableMs, bufferedMs, positionMs, seekToMs } = controls;
  const total = Math.max(totalMs, 1);
  const pct = (ms: number): number => barPercent(ms, totalMs);

  return (
    <div className="player__seek">
      <div className="player__rail">
        <div className="player__available" style={{ width: `${pct(availableMs)}%` }} />
        <div className="player__buffered" style={{ width: `${pct(bufferedMs)}%` }} />
        <div className="player__played" style={{ width: `${pct(positionMs)}%` }} />
      </div>
      <input
        type="range"
        className="player__range"
        min={0}
        max={total}
        step={1000}
        value={Math.min(positionMs, total)}
        aria-label="Oynatma konumu"
        aria-valuetext={`${formatDuration(positionMs)} / ${formatDuration(totalMs)}`}
        onChange={(event: ChangeEvent<HTMLInputElement>) => seekToMs(Number(event.target.value))}
      />
    </div>
  );
}

function VolumeControl({
  volume,
  muted,
  onToggleMute,
  onSetVolume,
}: {
  volume: number;
  muted: boolean;
  onToggleMute: () => void;
  onSetVolume: (val: number) => void;
}): ReactNode {
  const isSilent = muted || volume === 0;
  /*
   * Ses düzeyi ikonu artık emoji değil; seviye bilgisi bileşene geçiriliyor.
   * `muted` / `low` / `high` dalgaları ayrı ayrı çizilir, böylece kullanıcı
   * sesin kesildiğini ikonun kendisinden de görür (yalnız `aria-label`'a
   * güvenmek ekran okuyucu kullanıcılarına özgü bir çözüm olurdu).
   */
  const level = isSilent ? 'muted' : volume < 0.5 ? 'low' : 'high';

  return (
    <div className="player__volume-wrap">
      <button
        type="button"
        className="player__button player__button--volume"
        onClick={onToggleMute}
        aria-label={muted ? 'Sesi aç (M)' : 'Sesi kapat (M)'}
        title={muted ? 'Sesi aç (M)' : 'Sesi kapat (M)'}
      >
        <VolumeIcon level={level} size={18} />
      </button>
      <input
        type="range"
        className="player__volume"
        min={0}
        max={1}
        step={0.05}
        value={muted ? 0 : volume}
        aria-label="Ses düzeyi"
        onChange={(event) => onSetVolume(Number(event.target.value))}
      />
    </div>
  );
}

function SubtitlePopover({
  variant = 'popover',
  tracks,
  onSelect,
  prefs,
  onClose,
}: {
  variant?: 'popover' | 'sheet';
  tracks: Array<Subtitle & { selected: boolean; state?: SubtitleState }>;
  onSelect: (key: string | null) => void;
  prefs: SubtitlePrefsApi;
  onClose: () => void;
}): ReactNode {
  const [tab, setTab] = useState<'tracks' | 'style'>('tracks');
  const isSheet = variant === 'sheet';

  return (
    <div
      className={`player__popover ${isSheet ? 'player__popover--sheet' : ''}`}
      role="dialog"
      aria-label="Altyazı Menüsü"
      aria-modal={isSheet ? true : undefined}
    >
      {/* Alt sayfa tutamağı — mobilde "aşağı kapatılabilir" izlenimi verir. */}
      {isSheet && <div className="player__popover-grip" aria-hidden="true" />}

      <div className="player__popover-header">
        <div className="player__popover-tabs">
          <button
            type="button"
            className={`player__popover-tab ${tab === 'tracks' ? 'is-active' : ''}`}
            onClick={() => setTab('tracks')}
          >
            İzler
          </button>
          <button
            type="button"
            className={`player__popover-tab ${tab === 'style' ? 'is-active' : ''}`}
            onClick={() => setTab('style')}
          >
            Görünüm
          </button>
        </div>
        <button
          type="button"
          className="player__popover-close"
          onClick={onClose}
          aria-label="Kapat"
          title="Kapat"
        >
          <CloseIcon size={18} />
        </button>
      </div>

      {tab === 'tracks' ? (
        <div className="player__popover-list">
          <button
            type="button"
            className={`player__popover-item ${!tracks.some((t) => t.selected) ? 'is-selected' : ''}`}
            onClick={() => onSelect(null)}
          >
            <span>Kapalı</span>
            {!tracks.some((t) => t.selected) && <CheckIcon size={16} />}
          </button>
          {tracks.map((track) => {
            const lang = track.language
              ? (LANGUAGE_NAMES[track.language.toLowerCase()] ?? track.language.toUpperCase())
              : null;
            const label = track.title || lang || `İz ${track.key}`;
            return (
              <button
                key={track.key}
                type="button"
                className={`player__popover-item ${track.selected ? 'is-selected' : ''}`}
                onClick={() => onSelect(track.key)}
              >
                <div className="player__popover-item-title">
                  <span>{label}</span>
                  {track.forced && <span className="badge">Zorunlu</span>}
                  {track.external && (
                    <span className="badge" title="Filmin yanındaki ayrı altyazı dosyası">
                      Dosya
                    </span>
                  )}
                  {track.state === 'ready' && (
                    <span
                      className="badge badge--watched"
                      title="Altyazı hazır; seçince anında açılır"
                    >
                      Hazır
                    </span>
                  )}
                  {track.state === 'preparing' && (
                    <span className="badge badge--accent" title="Altyazı arka planda hazırlanıyor">
                      Hazırlanıyor
                    </span>
                  )}
                  {track.state === 'idle' && track.softPlayable && (
                    <span className="badge badge--muted" title="Seçince hazırlanır">
                      Hazırlanacak
                    </span>
                  )}
                  {!track.softPlayable && (
                    <span
                      className="badge badge--warn"
                      title="Blu-ray Resim Altyazısı (PGS/VobSub) - Tarayıcıda doğrudan metin olarak açılamaz, Uyumlulik Modunda videoya yakılmalıdır"
                    >
                      <ImageIcon size={13} />
                      PGS (Resim)
                    </span>
                  )}
                </div>
                {track.selected && <CheckIcon size={16} />}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="player__popover-styles">
          <div className="subtitle-prefs__row">
            <span className="subtitle-prefs__name">Boyut</span>
            <div className="subtitle-prefs__group">
              {(['small', 'medium', 'large'] as const).map((size) => (
                <button
                  key={size}
                  type="button"
                  className="subtitle-prefs__chip"
                  aria-pressed={prefs.prefs.size === size}
                  onClick={() => prefs.setSize(size)}
                >
                  {SIZE_LABELS[size]}
                </button>
              ))}
            </div>
          </div>

          <div className="subtitle-prefs__row">
            <span className="subtitle-prefs__name">Renk</span>
            <div className="subtitle-prefs__group">
              {(Object.keys(SUBTITLE_COLORS) as SubtitleColor[]).map((color) => (
                <button
                  key={color}
                  type="button"
                  className="subtitle-prefs__swatch"
                  aria-pressed={prefs.prefs.color === color}
                  title={SUBTITLE_COLORS[color].label}
                  style={{ background: SUBTITLE_COLORS[color].value }}
                  onClick={() => prefs.setColor(color)}
                />
              ))}
            </div>
          </div>

          <div className="subtitle-prefs__row subtitle-prefs__row--position">
            <span className="subtitle-prefs__name">Konum</span>
            <div className="subtitle-prefs__group subtitle-prefs__group--position">
              {/*
                Dikey kaydırma, **resmin alt kenarına** göre yüzde cinsinden.
                0 = resmin dibi, negatif = alttaki siyah banta iner, pozitif =
                görüntünün içine çıkar. Bkz. `computeSubtitleBottomPx`.
              */}
              <button
                type="button"
                className="subtitle-prefs__chip subtitle-prefs__chip--icon"
                aria-label="Altyazıyı aşağı çek"
                title="Aşağı çek (siyah banta doğru)"
                onClick={() =>
                  prefs.setPosition(clampPositionPercent(prefs.prefs.position - POSITION_STEP))
                }
                disabled={prefs.prefs.position <= POSITION_MIN_PERCENT}
              >
                <ChevronDownIcon size={18} />
              </button>
              <input
                type="range"
                className="subtitle-prefs__range"
                min={POSITION_MIN_PERCENT}
                max={POSITION_MAX_PERCENT}
                step={1}
                value={prefs.prefs.position}
                onChange={(e) => prefs.setPosition(Number(e.target.value))}
                aria-label="Altyazı dikey konumu"
                aria-valuetext={describePosition(prefs.prefs.position)}
              />
              <button
                type="button"
                className="subtitle-prefs__chip subtitle-prefs__chip--icon"
                aria-label="Altyazıyı yukarı çek"
                title="Yukarı çek (görüntünün içine)"
                onClick={() =>
                  prefs.setPosition(clampPositionPercent(prefs.prefs.position + POSITION_STEP))
                }
                disabled={prefs.prefs.position >= POSITION_MAX_PERCENT}
              >
                <ChevronUpIcon size={18} />
              </button>
              <span className="subtitle-prefs__position-value">
                {describePosition(prefs.prefs.position)}
              </span>
            </div>
            {/*
              "Siyah banta indir" yalnızca gerçekten bant varken anlamlı.
              16:9 kaynakta bant yoktur; düğme yanıltıcı olurdu.
            */}
            {prefs.hasLetterbox && (
              <button
                type="button"
                className="subtitle-prefs__reset"
                onClick={() => prefs.fitToLowerBar()}
                title="Altyazıyı alttaki siyah bandın ortasına yerleştir"
              >
                <ChevronDownIcon size={14} />
                Siyah banta
              </button>
            )}
          </div>

          <div className="subtitle-prefs__row">
            <label className="subtitle-prefs__name" htmlFor="popover-sub-bg">
              Arka Plan
            </label>
            <select
              id="popover-sub-bg"
              className="subtitle-bar__select"
              value={prefs.prefs.background}
              onChange={(e) => prefs.setBackground(e.target.value as SubtitleBackground)}
            >
              {(Object.keys(SUBTITLE_BACKGROUNDS) as SubtitleBackground[]).map((key) => (
                <option key={key} value={key}>
                  {SUBTITLE_BACKGROUNDS[key]}
                </option>
              ))}
            </select>
          </div>

          <div className="subtitle-prefs__row">
            <label className="subtitle-prefs__checkbox">
              <input
                type="checkbox"
                checked={prefs.prefs.bold}
                onChange={(e) => prefs.setBold(e.target.checked)}
              />
              Kalın Yazı
            </label>
            <button type="button" className="subtitle-prefs__reset" onClick={prefs.reset}>
              Varsayılan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShortcutsModal({ onClose }: { onClose: () => void }): ReactNode {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  return (
    <div className="player__modal-backdrop">
      <button type="button" className="player__modal-scrim" onClick={onClose} aria-label="Kapat" />
      <div
        ref={dialogRef}
        className="player__modal"
        role="dialog"
        aria-modal="true"
        aria-label="Klavye Kısayolları"
        tabIndex={-1}
      >
        <div className="player__modal-header">
          <h3>
            <KeyboardIcon size={17} />
            Klavye Kısayolları
          </h3>
          <button
            type="button"
            className="player__modal-close"
            onClick={onClose}
            aria-label="Kapat"
          >
            <CloseIcon size={18} />
          </button>
        </div>
        <div className="player__modal-body">
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>Boşluk</kbd> / <kbd>K</kbd>
            </span>
            <span className="shortcut-desc">Oynat / Duraklat</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>←</kbd> / <kbd>J</kbd>
            </span>
            <span className="shortcut-desc">10 saniye geri sar</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>→</kbd> / <kbd>L</kbd>
            </span>
            <span className="shortcut-desc">10 saniye ileri sar</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>↑</kbd> / <kbd>↓</kbd>
            </span>
            <span className="shortcut-desc">Ses düzeyini artır / azalt</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>M</kbd>
            </span>
            <span className="shortcut-desc">Sesi kapat / aç (Mute)</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>F</kbd>
            </span>
            <span className="shortcut-desc">Tam ekran aç / kapat</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>C</kbd>
            </span>
            <span className="shortcut-desc">Altyazıyı aç / kapat</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>N</kbd>
            </span>
            <span className="shortcut-desc">Sıradaki parçaya / bölüme geç</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>?</kbd>
            </span>
            <span className="shortcut-desc">Bu kısayol kılavuzunu aç / kapat</span>
          </div>
          <div className="shortcut-row">
            <span className="shortcut-keys">
              <kbd>Esc</kbd>
            </span>
            <span className="shortcut-desc">Tam ekrandan veya menüden çık</span>
          </div>
        </div>
      </div>
    </div>
  );
}
